import { useEffect, useRef } from "react";
import { Flex, Heading, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import type {
  Font,
  FontFamiliesResponse,
  FontFamily,
  FontsResponse,
} from "models/types/agency-settings";
import { useFormContext } from "react-hook-form";
import { FormField } from "components/form-field";
import {
  SelectInput,
  type MenuOption,
  type OptionsGroup,
} from "components/inputs/select-input";
import type { AppearanceFormValues } from "modules/agency-settings/models/account-branding-model";
import {
  FONT_WEIGHT_DESCRIPTORS,
  fontModel,
} from "modules/agency-settings/models/font-model";

const useResetFontWeightOnFontChange = (
  fontFamilyId: string | null,
  fieldName: "heading_font" | "body_font",
  availableFonts: MenuOption<Font>[],
) => {
  const { setValue, getValues } = useFormContext<AppearanceFormValues>();
  const previousFontFamilyId = useRef(fontFamilyId);

  useEffect(() => {
    const hasFontFamilyChanged = fontFamilyId !== previousFontFamilyId.current;

    if (hasFontFamilyChanged) {
      // Reset font weight if the selected font is not available
      setValue(fieldName, availableFonts[0]?.value);
    }

    previousFontFamilyId.current = fontFamilyId;
  }, [setValue, fieldName, availableFonts, getValues, fontFamilyId]);
};

function transformValueToMenuItemOptionValue(value: Font | FontFamily): string {
  return value.id;
}

export function TextStylesFontTab() {
  const { watch } = useFormContext<AppearanceFormValues>();

  // Fetch uploaded fonts
  const { data: uploadedFontsData } = useQuery<FontsResponse>(
    fontModel.queries.list(),
  );

  // Fetch Google fonts
  const { data: fontFamiliesData } = useQuery<FontFamiliesResponse>(
    fontModel.queries.listFontFamilies(),
  );

  const headerFontFamilyValue = watch("heading_font_family")?.id ?? null;
  const bodyFontFamilyValue = watch("body_font_family")?.id ?? null;

  // Prepare font family options for select inputs
  const fontFamilyOptions = (() => {
    const options: OptionsGroup<Font | FontFamily>[] = [];

    // Add Google Fonts group
    if (fontFamiliesData?.data) {
      options.push({
        label: { id: "google_fonts", value: "Google Fonts" },
        options: fontFamiliesData.data.map((family: FontFamily) => ({
          value: family,
          label: <Text fontFamily={family.name}>{family.name}</Text>,
        })),
      });
    }

    // Add Uploaded Fonts group
    if (uploadedFontsData?.data && uploadedFontsData.data.length > 0) {
      options.push({
        label: { id: "uploaded_fonts", value: "Uploaded Fonts" },
        options: uploadedFontsData.data.map((font: Font) => ({
          value: font,
          label: font.name,
        })),
      });
    }

    return options;
  })();

  // Get available fonts (weights) from the selected font family
  const getFontOptions = (
    fontOrFamilyId: string | null,
    type: "body" | "heading",
  ): OptionsGroup<Font>[] => {
    // Return empty array if no font name is selected
    if (!fontOrFamilyId)
      return [
        {
          label: { id: "weights", value: "Weights" },
          options: [],
        },
      ];

    // Find matching font family in the Google fonts
    const googleFontFamily = fontFamiliesData?.data?.find(
      (family) => family.id === fontOrFamilyId,
    );

    if (googleFontFamily) {
      // Return weights from Google font family
      return [
        {
          label: { id: "weights", value: "Weights" },
          options: googleFontFamily.fonts
            .filter(
              (font) =>
                font.style !== "italic" &&
                (type === "heading"
                  ? font.weight >= 600
                  : font.weight <= 400 && font.weight >= 300),
            )
            .map((font) => ({
              value: font,
              label: `${
                FONT_WEIGHT_DESCRIPTORS[
                  Number(font.weight) as keyof typeof FONT_WEIGHT_DESCRIPTORS
                ]
              } (${font.weight})`,
            })),
        },
      ];
    }

    const uploadedFont = uploadedFontsData?.data?.find(
      (font) => font.id === fontOrFamilyId,
    );

    if (uploadedFont) {
      const weightLabel =
        FONT_WEIGHT_DESCRIPTORS[
          Number(uploadedFont.weight) as keyof typeof FONT_WEIGHT_DESCRIPTORS
        ];

      return [
        {
          label: { id: "weights", value: "Weights" },
          options: [
            {
              value: uploadedFont,
              label: weightLabel,
            },
          ],
        },
      ];
    }

    return [
      {
        label: { id: "weights", value: "Weights" },
        options: [],
      },
    ];
  };

  const headerFontOptions = getFontOptions(headerFontFamilyValue, "heading");
  const bodyFontOptions = getFontOptions(bodyFontFamilyValue, "body");

  useResetFontWeightOnFontChange(
    headerFontFamilyValue,
    "heading_font",
    headerFontOptions[0]?.options,
  );
  useResetFontWeightOnFontChange(
    bodyFontFamilyValue,
    "body_font",
    bodyFontOptions[0]?.options,
  );

  return (
    <>
      <Flex direction={"column"} gap={2} mt={2}>
        <Heading fontSize={"lg"} as={"h4"}>
          Heading
        </Heading>
        <Text fontSize={"md"} color={"gray.600"}>
          Customise your header text used at the top of pages and for
          subheadings in sections
        </Text>
        <Flex direction={"row"} justify={"flex-start"} gap={2}>
          <FormField
            name={"heading_font_family"}
            required
            inputProps={{
              menuButtonProps: {
                borderWidth: "1px",
                borderColor: "gray.200",
                fontWeight: "normal",
                width: "100%",
              },
              menuProps: {
                placement: "top-start",
              },
              optionGroups: fontFamilyOptions,
              transformValueToMenuItemOptionValue:
                transformValueToMenuItemOptionValue,
            }}
            Input={SelectInput<FontFamily | Font>}
          />

          <FormField
            name={"heading_font"}
            required
            inputProps={{
              menuButtonProps: {
                borderWidth: "1px",
                borderColor: "gray.200",
                fontWeight: "normal",
                width: "100%",
              },
              optionGroups: headerFontOptions,
              transformValueToMenuItemOptionValue:
                transformValueToMenuItemOptionValue,
              menuProps: {
                placement: "top-start",
              },
            }}
            Input={SelectInput<Font>}
          />
        </Flex>
      </Flex>
      <Flex direction={"column"} gap={2}>
        <Heading fontSize={"lg"} as={"h4"}>
          Body
        </Heading>
        <Text fontSize={"md"} color={"gray.600"}>
          Customise your paragraph text
        </Text>
        <Flex direction={"row"} justify={"flex-start"} gap={2}>
          <FormField
            name={"body_font_family"}
            required
            inputProps={{
              menuButtonProps: {
                borderWidth: "1px",
                borderColor: "gray.200",
                fontWeight: "normal",
                width: "100%",
              },
              optionGroups: fontFamilyOptions,
              transformValueToMenuItemOptionValue:
                transformValueToMenuItemOptionValue,
              menuProps: {
                placement: "top-start",
              },
            }}
            Input={SelectInput<FontFamily | Font>}
          />

          <FormField
            name={"body_font"}
            required
            inputProps={{
              menuButtonProps: {
                borderWidth: "1px",
                borderColor: "gray.200",
                fontWeight: "normal",
                width: "100%",
              },

              optionGroups: bodyFontOptions,
              transformValueToMenuItemOptionValue:
                transformValueToMenuItemOptionValue,
              menuProps: {
                placement: "top-start",
              },
            }}
            Input={SelectInput<Font>}
          />
        </Flex>
      </Flex>
    </>
  );
}
